import {HttpClient} from '@angular/common/http';
import {Component, Input, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Observable, of, tap} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {ContactType} from './contact-type.enum';

@Component({
    selector: 'app-organization-contacts',
    template: `
    <div class="row justify-content-between" *ngIf="!this.isLoading && !this.showError">
      <div class="col-auto d-flex align-items-center">
        <button class="btn btn-primary mt-3 mx-3" [routerLink]="'../'"><span class="mdi mdi-arrow-left"></span> Back</button>
      </div>
      <div class="col-auto">
        <a class="btn btn-success mt-3 mx-3" data-bs-toggle="modal" data-bs-target="#Contact-modal" (click)="triggerCreateModal()">
          <i class="mdi mdi-plus-circle me-2"></i>
          Create New Contact
        </a>
      </div>
    </div>
    <div class="table-responsive">
      <table class="table">
        <thead class="table-light">
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th>Phone</th>
          <th>Type</th>
          <th></th>
        </tr>
        </thead>
        <tbody app-loading-shimmer [loading]="this.isLoading" [columns]="5">
        <tr *ngFor="let item of this.contacts$ | async">
          <td>{{ item.name }}</td>
          <td>{{ item.email }}</td>
          <td>{{ item.phone | phone }}</td>
          <td>{{ item.type }}</td>
          <td>
            <div class="row justify-content-end">
              <div class="col-auto px-1">
                <a class="action-icon mdi mdi-square-edit-outline fs-4" role="button" data-bs-toggle="modal" data-bs-target="#Contact-modal" (click)="triggerUpdateModal(item)"></a>
              </div>
              <div class="col-auto px-1">
                <a class="action-icon mdi mdi mdi-delete fs-4" role="button" (click)="deleteContact(item.id)"></a>
              </div>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
    <div *ngIf="this.showError" class="alert alert-danger" role="alert">
      <h4 class="alert-heading">An error occurred while fetching contacts.</h4>
      <hr class="my-2">
      <p class="mb-0">If the error persists please contact us at <a class="text-danger" href="mailto:{{this.supportEmail}}">{{ this.supportEmail }}</a>.
      </p>
    </div>

    <div id="Contact-modal" class="modal fade" tabindex="-1" style="display: none;" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header py-3 px-4 border-bottom-0">
            <h4 class="modal-title" id="standard-modalLabel">{{ this.isCreate ? 'Create New' : 'Update' }} Contact</h4>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"
                    (click)="closeModal()"></button>
          </div>
          <form class="ps-3 pe-3 needs-validation" [formGroup]="this.contactForm">
            <div class="modal-body">
              <div class="row g-2">
                <div class="col-12">
                  <div class="mb-3">
                    <label for="name" class="form-label">Name</label>
                    <input type="text" id="name" name="name" formControlName="name" class="form-control" required>
                  </div>
                  <div class="mb-3">
                    <label for="email" class="form-label">Email</label>
                    <input type="text" id="email" name="email" formControlName="email" class="form-control" required>
                  </div>
                  <div class="mb-3">
                    <label for="phone" class="form-label">Phone</label>
                    <input type="text" id="phone" name="phone" formControlName="phone" class="form-control" mask="(000) 000-0000" required>
                  </div>
                  <div class="mb-3">
                    <label for="organization_type" class="form-label">Type</label>
                    <select type="text" id="organization_type" name="organization_type" formControlName="type" class="form-control" required>
                      <option *ngFor="let type of this.contactTypeOptions" [value]="type">{{ type }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer d-block">
              <div class="row g-2 text-end">
                <div class="mb-3 col-12">
                  <button type="submit" class="btn btn-primary" (click)="this.isCreate? createContact() : updateContact()" [disabled]="this.isLoading">{{ this.isCreate ? 'Create' : 'Update' }}</button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  `,
    styles: ``,
    standalone: false
})
export class OrganizationContactsComponent implements OnInit {
  @Input({required: true})
  public contactsRoute!: string;
  protected readonly supportEmail = 'Support@DarkMatterIns.com';
  protected readonly contactTypeOptions = Object.values(ContactType);
  protected contacts$: Observable<Contact[]>;
  protected isLoading = true;
  protected showError = false;
  protected contactForm: FormGroup;
  protected isCreate = true;
  protected selectedContactId: string;

  constructor(private http: HttpClient, private formBuilder: FormBuilder) {}

  ngOnInit(): void {
    this.contacts$ = this.getContacts();
    this.contactForm = this.formBuilder.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required]],
      type: [ContactType.TECHNICAL_CONTACT, Validators.required]
    });
  }

  private getContacts(): Observable<Contact[]> {
    return this.http.get<{ items: Contact[], count: number}>(this.contactsRoute).pipe(
      switchMap(response => of(response.items)),
      tap(_ => this.isLoading = false),
      catchError(_ => {
        this.isLoading = false;
        this.showError = true;
        return of([]);
      })
    );
  }

  protected closeModal(): void {
    const element = document.getElementById('Contact-modal');
    // @ts-ignore
    const modal = bootstrap.Modal.getInstance(element);
    if (modal) {
      modal.hide();
    }
  }

  protected triggerCreateModal(): void {
    this.contactForm.reset({ type: ContactType.TECHNICAL_CONTACT });
    this.isCreate = true;
  }

  protected createContact(): void {
    this.contactForm.markAllAsTouched();
    this.contactForm.markAsDirty();

    if (this.contactForm.invalid) {
      return;
    }

    this.isLoading = true;
    this.contacts$ = this.http.post<any>(this.contactsRoute, this.contactForm.value).pipe(
      catchError(() => of()),
      tap(_ => this.closeModal()),
      switchMap(_ => this.getContacts()),
      tap(_ => {
        this.contactForm.reset({ type: ContactType.TECHNICAL_CONTACT });
      })
    );
  }

  protected triggerUpdateModal(contact: Contact): void {
    this.isCreate = false;
    this.selectedContactId = contact.id;
    this.contactForm.patchValue({
      name: contact.name,
      email: contact.email,
      phone: contact.phone,
      type: contact.type
    });
  }

  protected updateContact(): void {
    this.contactForm.markAllAsTouched();
    this.contactForm.markAsDirty();

    if (this.contactForm.invalid) {
      return;
    }

    this.isLoading = true;
    this.contacts$ = this.http.put(`${this.contactsRoute}/${this.selectedContactId}`, this.contactForm.value).pipe(
      catchError(() => of()),
      tap(_ => this.closeModal()),
      switchMap(_ => this.getContacts()),
      tap(_ => {
        this.contactForm.reset({ type: ContactType.TECHNICAL_CONTACT });
      })
    );
  }

  protected deleteContact(contactId: string): void {
    this.isLoading = true;
    this.contacts$ = this.http.delete(`${this.contactsRoute}/${contactId}`).pipe(
      catchError(() => of()),
      switchMap(_ => this.getContacts()),
    );
  }
}

interface Contact {
  id: string;
  name: string;
  email: string;
  phone: string;
  type: ContactType;
  updated_at: string;
  created_at: string;
}
